<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Embedded connect flow for a Zernio-brokered channel (Instagram DM / Messenger).
// The customer authorizes their OWN account through the partner's OAuth (a popup),
// never creating a Zernio account. We create a pending inbox first, then poll for
// the connected account and bind it — mirrors the Chakra connect flow.
const STATUS = Object.freeze({
  PENDING: 'pending',
  CONNECTED: 'connected',
  ERROR: 'error',
  // Facebook (headless) returns needing a Page pick — we render our own picker.
  SELECT_PAGE: 'select_page',
});
const POLL_INTERVAL_MS = 4000;

export default {
  components: { PageHeader, NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      step: 'form', // 'form' | 'connecting'
      inboxName: '',
      createdInboxId: null,
      connectionStatus: STATUS.PENDING,
      connectError: null,
      pollTimer: null,
      authWindow: null,
      authUrl: null,
      isSubmitting: false,
      pages: [],
      pagesLoading: false,
      tempToken: null,
      userProfile: null,
      connectToken: null,
    };
  },
  validations: {
    inboxName: { required },
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    platform() {
      const subPage = this.$route.params.sub_page || '';
      return subPage.replace('zernio_', '') || 'instagram';
    },
    platformLabel() {
      return this.platform === 'facebook' ? 'Messenger' : 'Instagram';
    },
    isConnecting() {
      return this.step === 'connecting';
    },
    isError() {
      return this.connectionStatus === STATUS.ERROR;
    },
    isPageSelection() {
      return this.connectionStatus === STATUS.SELECT_PAGE;
    },
  },
  mounted() {
    // The connect popup returns to our own /zernio/connected page, which posts
    // this message — bind + close immediately instead of waiting for the poll.
    window.addEventListener('message', this.onConnectMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onConnectMessage);
    this.stopPolling();
  },
  methods: {
    onConnectMessage(event) {
      const data = event?.data;
      if (data?.source !== 'metrica-social-connect') return;
      if (data.status === 'select_page') {
        this.enterPageSelection(data);
      } else if (data.status === 'connected') {
        this.checkStatus();
      }
    },
    enterPageSelection(data) {
      this.tempToken = data.tempToken;
      // Raw JSON string from the callback — round-tripped back to select-page.
      this.userProfile = data.userProfile;
      // The short-lived session token; select-page needs it as X-Connect-Token to
      // actually persist the account (Bearer alone lists pages but never connects).
      this.connectToken = data.connectToken;
      this.stopPolling();
      this.connectionStatus = STATUS.SELECT_PAGE;
      try {
        this.authWindow?.close();
      } catch (e) {
        // cross-origin; ignore
      }
      this.fetchPages();
    },
    async fetchPages() {
      this.pagesLoading = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/zernio_facebook_pages`,
          {
            params: {
              temp_token: this.tempToken,
              connect_token: this.connectToken,
            },
          }
        );
        this.pages = data.pages || [];
      } catch (error) {
        this.connectionStatus = STATUS.ERROR;
        this.connectError = error.message;
      } finally {
        this.pagesLoading = false;
      }
    },
    async selectPage(page) {
      this.isSubmitting = true;
      // Show the connecting state and start the /accounts poll as a safety net: if
      // select-page created the account on the partner side, the poll binds it even
      // when the select response itself isn't directly bindable.
      this.connectionStatus = STATUS.PENDING;
      this.startPolling();
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/zernio_select_page`,
          {
            page_id: page.id,
            temp_token: this.tempToken,
            user_profile: this.userProfile,
            connect_token: this.connectToken,
          }
        );
        if (data.status === STATUS.CONNECTED) {
          this.onConnected();
        } else if (data.status === STATUS.ERROR) {
          this.stopPolling();
          this.connectionStatus = STATUS.ERROR;
          this.connectError = data.error;
        }
        // pending: leave the poll running — it completes once the account appears
      } catch (error) {
        // network error — keep the poll running as the fallback
      } finally {
        this.isSubmitting = false;
      }
    },
    onConnected() {
      this.connectionStatus = STATUS.CONNECTED;
      this.stopPolling();
      try {
        this.authWindow?.close();
      } catch (e) {
        // cross-origin; ignore
      }
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.createdInboxId },
      });
    },
    async startConnect() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      // Open the popup synchronously (inside the user gesture) so it isn't
      // blocked; we point it at the authUrl once the backend returns it.
      const popup = window.open('', 'zernio_connect', 'width=620,height=760');
      this.authWindow = popup;
      this.isSubmitting = true;
      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName?.trim(),
          channel: { type: 'zernio', platform: this.platform },
        });
        this.createdInboxId = inbox.id;

        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${inbox.id}/zernio_connect`
        );
        this.authUrl = data.auth_url;
        if (popup && !popup.closed) {
          popup.location.href = this.authUrl;
        }
        this.step = 'connecting';
        this.startPolling();
      } catch (error) {
        if (popup) {
          try {
            popup.close();
          } catch (e) {
            // cross-origin; ignore
          }
        }
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.ERROR_MESSAGE')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(this.checkStatus, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async checkStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/zernio_status`
        );
        if (data.status === STATUS.CONNECTED) {
          this.onConnected();
        } else if (data.status === STATUS.ERROR) {
          this.connectionStatus = STATUS.ERROR;
          this.connectError = data.error;
          this.stopPolling();
        }
      } catch (e) {
        // transient error — keep polling
      }
    },
    retry() {
      this.connectionStatus = STATUS.PENDING;
      this.connectError = null;
      if (this.authUrl) {
        this.authWindow = window.open(
          this.authUrl,
          'zernio_connect',
          'width=620,height=760'
        );
      }
      this.startPolling();
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.DESC')"
    />

    <form
      v-if="!isConnecting"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="startConnect()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isSubmitting"
          type="submit"
          solid
          blue
          :label="
            $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECT_BUTTON', {
              platform: platformLabel,
            })
          "
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-start gap-3 mt-4 max-w-lg">
      <template v-if="isError">
        <p class="text-n-ruby-11">
          {{
            connectError || $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.ERROR')
          }}
        </p>
        <NextButton
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.RETRY')"
          @click="retry"
        />
      </template>
      <template v-else-if="isPageSelection">
        <p class="font-medium">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.SELECT_PAGE.TITLE') }}
        </p>
        <p class="text-n-slate-11 text-sm">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.SELECT_PAGE.DESC') }}
        </p>
        <p v-if="pagesLoading" class="text-n-slate-11 text-sm">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.SELECT_PAGE.LOADING') }}
        </p>
        <p v-else-if="!pages.length" class="text-n-slate-11 text-sm">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.SELECT_PAGE.EMPTY') }}
        </p>
        <div v-else class="flex flex-col w-full gap-2">
          <button
            v-for="page in pages"
            :key="page.id"
            type="button"
            :disabled="isSubmitting"
            class="flex flex-col items-start w-full px-4 py-3 text-left transition-colors border rounded-lg border-n-weak hover:border-n-brand hover:bg-n-alpha-1 disabled:opacity-50"
            @click="selectPage(page)"
          >
            <span class="font-medium text-n-slate-12">{{ page.name }}</span>
            <span v-if="page.username" class="text-xs text-n-slate-11">
              {{ `@${page.username}` }}
            </span>
          </button>
        </div>
      </template>
      <template v-else>
        <p class="font-medium">
          {{
            $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.TITLE', {
              platform: platformLabel,
            })
          }}
        </p>
        <p class="text-n-slate-11 text-sm">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.DESC') }}
        </p>
        <a
          v-if="authUrl"
          :href="authUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-blue-11 underline text-sm"
        >
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.REOPEN') }}
        </a>
      </template>
    </div>
  </div>
</template>
